import { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * The order whose details panel is open lives in the query string rather than in state alone, so the
 * device back gesture closes the panel instead of leaving the page, and a reload or a shared link
 * reopens the same order.
 */
const ORDER_PARAM = 'order';

/** A hand-edited or truncated param opens nothing, rather than a panel stuck loading order `NaN`. */
const parseOrderId = (param: string | null): number | null => {
  const id = Number(param);

  return param && Number.isInteger(id) && id > 0 ? id : null;
};

/** The query string with the panel's own param taken out, for comparing one entry's state to another's. */
const withoutOrder = (params: URLSearchParams): string => {
  const rest = new URLSearchParams(params);
  rest.delete(ORDER_PARAM);
  rest.sort();

  return rest.toString();
};

type OrderDetailsParam = {
  /** The order to show, or null when the panel is closed. */
  orderId: number | null;
  openOrder: (id: string) => void;
  closeOrder: () => void;
};

export const useOrderDetailsParam = (): OrderDetailsParam => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = parseOrderId(searchParams.get(ORDER_PARAM));

  // True while the history entry for the open panel is one we pushed ourselves.
  const hasPushedRef = useRef(false);
  // The rest of the query string as it stood when we pushed. Popping that entry restores whatever it
  // held, so anything replaced into it since — the selected day above all — would be undone by the
  // close button. Comparing against this is how we tell a pop that only closes the panel from one
  // that would also rewind the view underneath it.
  const otherParamsAtPushRef = useRef('');

  useEffect(() => {
    if (orderId === null) {
      hasPushedRef.current = false;
    }
  }, [orderId]);

  const openOrder = useCallback(
    (id: string) => {
      // Only the first open pushes. Picking a second order while the panel is already up replaces
      // that entry rather than stacking another one on it — otherwise closing would pop back to the
      // order before it and leave the panel open, needing one press per order ever opened.
      const isFirstOpen = orderId === null;

      if (isFirstOpen) {
        hasPushedRef.current = true;
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(ORDER_PARAM, id);

          if (isFirstOpen) {
            otherParamsAtPushRef.current = withoutOrder(prev);
          }

          return next;
        },
        // That one pushed entry is what makes the back gesture close the panel. Every other param —
        // the orders page's `?q=` above all — is carried over rather than replaced.
        { replace: !isFirstOpen }
      );
    },
    [orderId, setSearchParams]
  );

  const closeOrder = useCallback(() => {
    // Popping our own entry keeps the close button and the device gesture the same single motion. A
    // panel opened by a deep link has no entry of ours to pop, so it drops the param in place instead.
    //
    // And only while the rest of the query string still matches what that entry holds: changing the
    // day with the panel open replaces into it, and popping would then quietly rewind the day too —
    // the close button moving the list underneath it. Dropping the param in place costs a stale entry
    // in the history stack, which is the cheaper of the two.
    if (hasPushedRef.current && withoutOrder(searchParams) === otherParamsAtPushRef.current) {
      navigate(-1);

      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(ORDER_PARAM);

        return next;
      },
      { replace: true }
    );
  }, [navigate, searchParams, setSearchParams]);

  return { orderId, openOrder, closeOrder };
};
